import {
  FileBarChart,
  Fuel,
  FileText,
  Flag,
  Globe,
  ActivitySquare,
  Waves,
  Settings,
  ArrowRight,
  Building2,
  Ship,
  LineChart,
  ClipboardCheck,
} from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AdditivesPerformance from "./AdditivesPerformance";

const modules = [
    {
    category: "IMO Regulations",
    icon: Ship,
    description: "International Maritime Organization standards",
    color: "#8b5cf6",
    items: [
      {
        title: "IMO / DCS",
        description: "Fuel consumption data collection system",
        icon: Globe,
        link: "/compliance/imo-dcs",
        cardColor: "#06b6d4",
      },
      {
        title: "CII",
        description: "Carbon Intensity Indicator tracking",
        icon: ActivitySquare,
        link: "/compliance/cii",
        cardColor: "#8b5cf6",
      },
    ],
  },
  {
    category: "EU Regulations",
    icon: Building2,
    description: "European Union maritime compliance standards",
    color: "#3b82f6",
    items: [
      {
        title: "EU ETS",
        description: "Emissions Trading System tracking and compliance",
        icon: FileBarChart,
        link: "/compliance/eu-ets",
        cardColor: "#6366f1",
      },
      {
        title: "Fuel EU",
        description: "Renewable fuel usage compliance monitoring",
        icon: Fuel,
        link: "/compliance/fuel-eu",
        cardColor: "#f59e0b",
      },
      {
        title: "EU MRV",
        description: "Monitoring, Reporting & Verification system",
        icon: FileText,
        link: "/compliance/eu-mrv",
        cardColor: "#10b981",
      },
    ],
  },
  {
    category: "UK Regulations",
    icon: Flag,
    description: "United Kingdom specific maritime regulations",
    color: "#ef4444",
    items: [
      {
        title: "UK ETS",
        description: "UK Emissions Trading System compliance",
        icon: Waves,
        link: "/compliance/uk-ets",
        cardColor: "#3b82f6",
      },
      {
        title: "UK MRV",
        description: "UK Monitoring, Reporting & Verification",
        icon: FileText,
        link: "/compliance/uk-mrv",
        cardColor: "#ef4444",
      },
    ],
  },
  {
    category: "Additives Performance",
    icon: LineChart,
    description: "Performance insights and optimization tools",
    color: "#14b8a6",
    directLink: "/compliance/additiveperformance",
    items: [
      {
        title: "Additives Performance",
        description: "Advanced compliance analytics dashboard",
        icon: Settings,
        link: "/compliance/additiveperformance",
        cardColor: "#14b8a6",
      },
    ],
  },
];

const ComplianceOverview = () => {
  const [hoveredCard, setHoveredCard] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(0);
  const navigate = useNavigate();

  const handleCardClick = (link) => {
    navigate(link);
  };

  const handleCategoryClick = (index) => {
    setSelectedCategory(index);
  };

  // Check if the selected category is "Additives Performance"
  const isAdditivesPerformance =
    modules[selectedCategory]?.category === "Additives Performance";

  return (
    <div className="relative min-h-screen">
      <div className="max-w-7xl mx-auto pr-4">
        <div className="flex items-center gap-2 mb-7">
          <ClipboardCheck className="w-8 h-8 text-[#1a325d]" />
          <div className="flex items-baseline gap-2">
            <div className="flex items-center gap-1">
              <span className="text-2xl font-semibold text-[#27ade3]">
                Compliance
              </span>
            </div>
          </div>
        </div>

        <div className="flex flex-col space-y-4 flex-row items-center justify-between">
          {/* Segmented control */}
          <div className="inline-flex p-1.5 bg-gray-50 rounded-xl border border-gray-200 shadow-sm overflow-auto">
            {modules.map((category, index) => (
              <button
                key={category.value}
                className={`px-6 py-3 rounded-xl text-lg font-semibold transition-all duration-300 flex items-center relative group ${
                  selectedCategory === index
                    ? "text-white bg-gradient-to-r from-blue-600 to-blue-700 shadow-lg"
                    : "text-gray-700 hover:text-blue-600 bg-white hover:bg-blue-50"
                }`}
                onClick={() => handleCategoryClick(index)}
              >
                {/* Active indicator bar */}
                {selectedCategory === index && (
                  <span className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-8 h-1 bg-blue-500 rounded-full"></span>
                )}

                <span className="relative z-10 flex items-center gap-3">
                  <category.icon
                    className={`w-6 h-6 transition-colors ${
                      selectedCategory === index
                        ? "text-white"
                        : "text-gray-500 group-hover:text-blue-500"
                    }`}
                  />
                  {category.category}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Render either the AdditivesPerformance component or the regular content */}
        {isAdditivesPerformance ? (
          <div className="mt-6">
            <AdditivesPerformance />
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3 my-6">
              {(() => {
                const IconComponent = modules[selectedCategory].icon;
                return (
                  <IconComponent
                    className="w-8 h-8"
                    style={{ color: modules[selectedCategory].color }}
                  />
                );
              })()}
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {modules[selectedCategory].category}
                </h2>
                <p className="text-gray-600 text-sm">
                  {modules[selectedCategory].description}
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              {modules[selectedCategory].items.map((mod, modIndex) => (
                <div
                  key={modIndex}
                  className="w-full sm:w-[48%] md:w-[30%] lg:w-[22%] min-w-[200px] h-[250px]"
                  onMouseEnter={() => setHoveredCard(modIndex)}
                  onMouseLeave={() => setHoveredCard(null)}
                >
                  <div className="relative w-full h-full transition-transform duration-500 ease-in-out transform-style-preserve-3d">
                    {/* Front Side */}
                    <div
                      className={`absolute w-full h-full backface-hidden rounded-xl cursor-pointer bg-gradient-to-tr from-white to-[#d9ebff] shadow-md overflow-hidden transition-all ${
                        hoveredCard === modIndex
                          ? "rotate-y-180 opacity-0"
                          : "rotate-y-0 opacity-100"
                      }`}
                      onClick={() => handleCardClick(mod.link)}
                      style={{
                        transform:
                          hoveredCard === modIndex
                            ? "rotateY(180deg)"
                            : "rotateY(0deg)",
                      }}
                    >
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                        <div
                          className="w-16 h-16 rounded-xl flex items-center justify-center mb-4"
                          style={{
                            background: `linear-gradient(135deg, ${mod.cardColor} 0%, ${mod.cardColor}80 100%)`,
                            boxShadow: `0 4px 12px ${mod.cardColor}30`,
                          }}
                        >
                          <mod.icon size={28} color="white" />
                        </div>
                        <h3 className="text-lg font-bold text-gray-800 mb-2">
                          {mod.title}
                        </h3>
                        <button
                          className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
                          style={{
                            background: `${mod.cardColor}10`,
                            color: mod.cardColor,
                          }}
                        >
                          Explore
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Back Side */}
                    <div
                      className={`absolute w-full h-full backface-hidden rounded-xl cursor-pointer bg-gray-800 text-white shadow-lg overflow-hidden transition-all ${
                        hoveredCard === modIndex
                          ? "rotate-y-0 opacity-100"
                          : "rotate-y-180 opacity-0"
                      }`}
                      onClick={() => handleCardClick(mod.link)}
                      style={{
                        transform:
                          hoveredCard === modIndex
                            ? "rotateY(0deg)"
                            : "rotateY(180deg)",
                      }}
                    >
                      <div className="flex flex-col items-center justify-center h-full p-6 text-center">
                        <div
                          className="w-14 h-14 rounded-lg flex items-center justify-center mb-4"
                          style={{
                            background: `linear-gradient(135deg, ${mod.cardColor} 0%, ${mod.cardColor}80 100%)`,
                          }}
                        >
                          <mod.icon size={24} color="white" />
                        </div>
                        <h3 className="text-lg font-bold mb-2">{mod.title}</h3>
                        <p className="text-sm text-gray-300 mb-6 line-clamp-3">
                          {mod.description}
                        </p>
                        <button
                          className="px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-1"
                          style={{
                            background: mod.cardColor,
                            color: "white",
                          }}
                        >
                          Access Now
                          <ArrowRight size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style jsx>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .transform-style-preserve-3d {
          transform-style: preserve-3d;
        }
        .backface-hidden {
          backface-visibility: hidden;
        }
        .rotate-y-180 {
          transform: rotateY(180deg);
        }
        .rotate-y-0 {
          transform: rotateY(0deg);
        }
      `}</style>
    </div>
  );
};

export default ComplianceOverview;
